import React from 'react';
import RenderSurveyQuestions from '../../../containers/crecer/surveys/RenderSurveyQuestions';
import { Button } from 'semantic-ui-react';
import Form from '../../../components/form/Form';
import {
  spiritualQuestions,
  discQuestions,
  sacredPathQuestions,
  leadersImmersionQuestions,
} from '../../../containers/crecer/surveys/surveysQuestions';
import Swal from 'sweetalert2';
import crecerForms from '../../../services/api/crecer';
import { errorInput, uniqueElements2Arrays, countAnswers } from '../../../components/helpers';
import {
  useRecaptchaToken,
  isCaptchaRequired,
  RECAPTCHA_SECURITY_ERROR,
} from '../../../components/helpers/recaptchaUtils';
import queryString from 'query-string';
import { InPersonStudentResults } from '../../../containers/crecer/surveys/inPersonStudentResults';
import { Margin, usePDF } from 'react-to-pdf';
import { habeasData } from '../../../components/helpers/habeasData';
import { RegistrationForm } from 'src/assets/styles/components/Registration';
import InitialSurveyForm from '../../../containers/crecer/surveys/initialSurveyForm';
import { Clase3Component } from 'src/assets/styles/components/clase3QuestionsStyles';
import { renderQuestions } from 'src/components/helpers/renderFormQuestions';

const SURVEY_META = {
  disc: {
    title: 'Conectar con tu personalidad',
    short: 'Personalidad',
    questions: discQuestions,
    options: [1, 2, 3, 4, 5],
    optionLabels: { low: 'Nunca', high: 'Siempre' },
    scale: [
      { value: 1, label: 'Nunca' },
      { value: 2, label: 'Rara vez' },
      { value: 3, label: 'A veces' },
      { value: 4, label: 'A menudo' },
      { value: 5, label: 'Siempre' },
    ],
    intro:
      'Califica cada afirmación según qué tan frecuente te describe. No hay respuestas correctas o incorrectas.',
  },
  spiritualgifts: {
    title: 'Conectar con tus dones espirituales',
    short: 'Dones',
    questions: spiritualQuestions,
    options: [1, 2, 3],
    optionLabels: { low: 'Casi nunca', high: 'Casi siempre' },
    scale: [
      { value: 1, label: 'Casi nunca' },
      { value: 2, label: 'Algunas veces' },
      { value: 3, label: 'Casi siempre' },
    ],
    intro: 'Califica cada afirmación según qué tan bien te describe.',
  },
  experienceWithGod: {
    title: 'Tu experiencia con Dios',
    short: 'Experiencia',
    questions: sacredPathQuestions,
    options: [0, 1, 2, 3],
    optionLabels: { low: 'No es cierto', high: 'Definitivamente verdadero' },
    scale: [
      { value: 0, label: 'No es cierto' },
      { value: 1, label: 'Algo cierto' },
      { value: 2, label: 'Verdadero' },
      { value: 3, label: 'Definitivamente verdadero' },
    ],
    intro:
      'Este test analiza cómo experimentas a Dios. Responde desde lo que es, no desde lo que te gustaría que fuera.',
  },
  additionalInformation: {
    title: 'Preguntas adicionales',
    short: 'Adicionales',
    intro: 'Por favor responde las siguientes preguntas adicionales con sinceridad.',
  },
};

const FLOW_LABELS = {
  initial: 'Inicio',
  guestInfo: 'Tus datos',
  disc: 'Personalidad',
  spiritualgifts: 'Dones',
  experienceWithGod: 'Experiencia',
  additionalInformation: 'Adicionales',
  sending: 'Enviando',
  results: 'Resultados',
};

const hydrateFromSurvey = (survey = {}, allowedKeys = []) => {
  const allow = (key) => !allowedKeys.length || allowedKeys.includes(key);

  return {
    DISCAnswers: allow('disc') ? survey.DISCAnswers || {} : {},
    spiritualGiftAnswers: allow('spiritualgifts') ? survey.spiritualGiftAnswers || {} : {},
    experienceWithGodAnswers: allow('experienceWithGod')
      ? survey.experienceWithGodAnswers || {}
      : {},
    additionalInformation: allow('additionalInformation') ? survey.additionalInformation || {} : {},
  };
};

const Survey = (props) => {
  const [student, set_student] = React.useState({});
  const [spiritualGiftAnswers, set_spiritualGiftAnswers] = React.useState({});
  const [DISCAnswers, set_DISCAnswers] = React.useState({});
  const [experienceWithGodAnswers, set_experienceWithGodAnswers] = React.useState({});
  const [additionalInformation, set_additionalInformation] = React.useState({});
  const [step, set_step] = React.useState('initial');
  const [errorInputs, set_errorInputs] = React.useState({});
  const [answersLeft, set_answersLeft] = React.useState([]);
  const [viewingPrevious, set_viewingPrevious] = React.useState(false);
  const [isRetake, set_isRetake] = React.useState(false);
  const [surveyDate, set_surveyDate] = React.useState(null);
  const [completedSurveys, set_completedSurveys] = React.useState([]);
  const getCaptchaToken = useRecaptchaToken('survey');
  const captchaRequired = isCaptchaRequired();
  const { toPDF, targetRef } = usePDF({
    method: 'save',
    page: { margin: Margin.MEDIUM },
    filename: 'resultados-encuestas-personalidad.pdf',
  });

  let query = props.location.search;
  let parsedQS = queryString.parse(query);
  const allSurveys = parsedQS?.surveys?.split?.(',')?.filter(Boolean) || [];

  React.useEffect(() => {
    if (parsedQS?.identification || parsedQS?.phone) {
      set_student((prev) => ({
        ...prev,
        identification: parsedQS?.identification || prev.identification,
        phone: parsedQS?.phone || prev.phone,
      }));
    }
    if (parsedQS?.additionalQuestions) {
      set_additionalInformation((prev) => ({
        ...prev,
        additionalQuestions: parsedQS?.additionalQuestions,
      }));
    }
  }, []);

  React.useEffect(() => {
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
    if (step === 'sending') {
      sendAnswers();
    }
  }, [step]);

  const applySurveyAnswers = (survey = {}, allowedKeys = []) => {
    const hydrated = hydrateFromSurvey(survey, allowedKeys);
    set_DISCAnswers(hydrated.DISCAnswers);
    set_spiritualGiftAnswers(hydrated.spiritualGiftAnswers);
    set_experienceWithGodAnswers(hydrated.experienceWithGodAnswers);
    set_additionalInformation((prev) => ({
      ...hydrated.additionalInformation,
      ...(parsedQS?.additionalQuestions
        ? { additionalQuestions: parsedQS.additionalQuestions }
        : {}),
      ...(!hydrated.additionalInformation?.additionalQuestions && prev.additionalQuestions
        ? { additionalQuestions: prev.additionalQuestions }
        : {}),
    }));
  };

  const clearRequestedSurveyAnswers = () => {
    if (!allSurveys.length || allSurveys.includes('disc')) set_DISCAnswers({});
    if (!allSurveys.length || allSurveys.includes('spiritualgifts')) set_spiritualGiftAnswers({});
    if (!allSurveys.length || allSurveys.includes('experienceWithGod')) {
      set_experienceWithGodAnswers({});
    }
    if (!allSurveys.length || allSurveys.includes('additionalInformation')) {
      set_additionalInformation((prev) =>
        parsedQS?.additionalQuestions ? { additionalQuestions: parsedQS.additionalQuestions } : {}
      );
    }
  };

  const handleLookupSuccess = (data, credentials = {}) => {
    const answer = data?.Answer && data.Answer.id ? data.Answer : {};
    const previousSurvey = data?.previousSurvey || answer?.CrecerSurveys?.[0] || null;

    set_student({
      ...credentials,
      ...answer,
      identification: answer.identification || credentials.identification,
      phone: answer.phone || credentials.phone,
    });
    set_completedSurveys(data?.completedSurveys || []);
    set_errorInputs({});

    if (data?.hasPreviousAnswers && previousSurvey) {
      const keys = data.completedSurveys?.length ? data.completedSurveys : allSurveys;
      applySurveyAnswers(previousSurvey, keys);
      set_surveyDate(data.answeredAt || previousSurvey.updatedAt || previousSurvey.createdAt);
      set_viewingPrevious(true);
      set_isRetake(false);
      set_step('results');
      return;
    }

    set_viewingPrevious(false);
    set_isRetake(false);
    set_surveyDate(null);
    set_step('guestInfo');
  };

  const startRetake = () => {
    clearRequestedSurveyAnswers();
    set_viewingPrevious(false);
    set_isRetake(true);
    set_surveyDate(null);
    set_answersLeft([]);
    set_errorInputs({});
    set_step('guestInfo');
  };

  const sendAnswers = async () => {
    let DISC_letter = countAnswers(DISCAnswers)?.[0]?.letter;

    const captcha = await getCaptchaToken({ skip: !captchaRequired });
    if (captchaRequired && !captcha) {
      Swal.fire(RECAPTCHA_SECURITY_ERROR);
      set_step(isRetake ? 'guestInfo' : 'initial');
      return;
    }

    let body = {
      spiritualGiftAnswers,
      DISCAnswers,
      experienceWithGodAnswers,
      DISC_letter,
      additionalInformation,
      surveys: allSurveys,
      campus: parsedQS?.campus || student?.campus || null,
      retake: isRetake,
      ...student,
      captcha,
    };

    crecerForms
      .saveSurveyAnswers(body)
      .then((response) => {
        if (response.data.success) {
          set_viewingPrevious(false);
          set_isRetake(false);
          set_surveyDate(
            response.data.savedAnswers?.updatedAt ||
              response.data.savedAnswers?.createdAt ||
              new Date().toISOString()
          );
          set_step('results');
        } else {
          Swal.fire(response.data.message || 'Hubo un error, por favor intenta más tarde.');
          set_step(isRetake ? 'guestInfo' : 'initial');
        }
      })
      .catch(() => {
        Swal.fire('Hubo un error, por favor intenta más tarde.');
        set_step(isRetake ? 'guestInfo' : 'initial');
      });
  };

  const verifyGuestForm = () => {
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
    let mandatoryInputs = ['name', 'lastName', 'identification', 'phone', 'email'];
    let errorInputs = {};

    mandatoryInputs.forEach((input) => {
      errorInputs = errorInput({
        errorInputs,
        input: { name: input, value: student[input] },
      });
    });

    if (student['identification'] && student['identification'].length <= 5) {
      errorInputs = errorInput({
        errorInputs,
        input: {
          name: 'identification',
          value: student['identification'].length >= 5,
        },
      });
    }

    if (student['phone'] && student['phone'].length <= 10) {
      errorInputs = errorInput({
        errorInputs,
        input: {
          name: 'phone',
          value: student['phone'].length >= 10,
        },
      });
    }

    set_errorInputs(errorInputs);
    return Object.values(errorInputs).includes(true);
  };

  const verifyAnswers = (questions, answers, errorKey) => {
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;

    const answersLefts = uniqueElements2Arrays(
      questions.map((q) => q.num),
      Object.keys(answers).map((e) => parseInt(e))
    );
    set_answersLeft(answersLefts);

    const nextErrors = errorInput({
      errorInputs: {},
      input: { name: errorKey, value: !answersLefts.length },
    });
    set_errorInputs(nextErrors);

    if (answersLefts.length) {
      requestAnimationFrame(() => {
        document.getElementById(`survey-q-${answersLefts[0]}`)?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      });
    }

    return Object.values(nextErrors).includes(true);
  };

  const verifyDISCForm = () => verifyAnswers(discQuestions, DISCAnswers, 'DISCAnswers');

  const verifySpiritualGiftsForm = () =>
    verifyAnswers(spiritualQuestions, spiritualGiftAnswers, 'spiritualGiftAnswers');

  const verifyExperienceWithGodForm = () =>
    verifyAnswers(sacredPathQuestions, experienceWithGodAnswers, 'experienceWithGodAnswers');

  const handleInputs = (event) => {
    let value = event.target.value;
    let name = event.target.name;
    set_student({ ...student, [name]: value });
  };

  const flowSteps = React.useMemo(
    () => ['guestInfo', ...allSurveys.filter(Boolean)],
    [parsedQS?.surveys]
  );

  const currentFlowIndex = Math.max(
    0,
    flowSteps.findIndex((s) => s === step)
  );

  const goNextSurvey = (currentKey) => {
    const next = allSurveys?.[allSurveys?.indexOf(currentKey) + 1];
    set_step(next || 'sending');
  };

  const renderAdditionalQuestions = () => {
    if (parsedQS?.additionalQuestions === 'leadersImmersion') {
      return leadersImmersionQuestions(additionalInformation);
    }
    return null;
  };

  const questionsLeftBanner = (missing = answersLeft) => {
    if (!missing.length) return null;
    return (
      <div className="survey-alert" role="alert">
        <strong>
          Falta{missing.length !== 1 ? 'n' : ''} {missing.length} pregunta
          {missing.length !== 1 ? 's' : ''}
        </strong>
        <p>
          Completa las preguntas{' '}
          {missing.slice(0, 12).map((answer, i) => (
            <button
              key={answer}
              type="button"
              className="survey-alert__link"
              onClick={() =>
                document.getElementById(`survey-q-${answer}`)?.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center',
                })
              }
            >
              {answer}
              {i < Math.min(missing.length, 12) - 1 ? ', ' : ''}
            </button>
          ))}
          {missing.length > 12 ? ` y ${missing.length - 12} más` : ''} para continuar.
        </p>
      </div>
    );
  };

  const renderProgress = () => {
    if (step === 'initial' || step === 'sending' || step === 'results' || allSurveys.length === 0)
      return null;

    const label = FLOW_LABELS[step] || SURVEY_META[step]?.short || 'Encuesta';
    const stepNumber = currentFlowIndex + 1;

    return (
      <div className="survey-progress">
        <div className="survey-progress__meta">
          <span className="survey-progress__label">{label}</span>
          <span className="survey-progress__count">
            Paso {stepNumber} de {flowSteps.length}
          </span>
        </div>
        <div className="survey-progress__bars">
          {flowSteps.map((flowStep, index) => (
            <div
              key={flowStep}
              className={`survey-progress__bar${index < currentFlowIndex ? ' is-done' : ''}${
                index === currentFlowIndex ? ' is-current' : ''
              }`}
            />
          ))}
        </div>
      </div>
    );
  };

  const renderScaleLegend = (scale) => (
    <div className="scale-legend">
      {scale.map((item) => (
        <div key={item.value} className="scale-legend__item">
          <span className="scale-legend__value">{item.value}</span>
          <span className="scale-legend__label">{item.label}</span>
        </div>
      ))}
    </div>
  );

  const renderAnswerProgress = (answeredCount, total) => {
    const pct = total ? Math.round((answeredCount / total) * 100) : 0;
    return (
      <div className="answer-progress">
        <div className="answer-progress__meta">
          <span>
            {answeredCount} de {total} respondidas
          </span>
          <span>{pct}%</span>
        </div>
        <div className="answer-progress__track">
          <div className="answer-progress__fill" style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  };

  const renderSurveyStep = ({ stepKey, answers, setAnswers, errorKey, verify }) => {
    const meta = SURVEY_META[stepKey];
    if (!meta) return null;

    const answeredCount = Object.keys(answers).length;
    const total = meta.questions.length;
    const liveUnanswered = errorInputs[errorKey]
      ? meta.questions.map((q) => q.num).filter((num) => answers[num] == null)
      : [];

    return (
      <div className="survey-step">
        <header className="survey-step__header">
          <h2>{meta.title}</h2>
          <p>{meta.intro}</p>
        </header>
        {renderScaleLegend(meta.scale)}
        {errorInputs[errorKey] && liveUnanswered.length > 0 && questionsLeftBanner(liveUnanswered)}
        {renderAnswerProgress(answeredCount, total)}
        <RenderSurveyQuestions
          set_answers={setAnswers}
          answers={answers}
          numberOptions={meta.options}
          questions={meta.questions}
          unanswered={liveUnanswered}
          optionLabels={meta.optionLabels}
        />
        <div className="survey-actions">
          <Button primary onClick={() => (verify() ? set_step(stepKey) : goNextSurvey(stepKey))}>
            Continuar
          </Button>
        </div>
      </div>
    );
  };

  if (allSurveys?.length === 0) {
    return (
      <Clase3Component>
        <div className="Questions survey-shell">
          <div className="survey-empty">
            <h1>Encuestas de personalidad</h1>
            <p>Hay un error con el link, por favor revisa e intenta nuevamente.</p>
          </div>
        </div>
      </Clase3Component>
    );
  }

  return (
    <Clase3Component>
      <div className="Questions survey-shell">
        <div ref={targetRef} className="survey-content">
          <header className="survey-page-header">
            <p className="survey-eyebrow">Crecer</p>
            <h1>Encuestas de personalidad</h1>
            {parsedQS?.campus && <p className="survey-campus">Campus {parsedQS.campus}</p>}
          </header>

          {renderProgress()}

          {step === 'initial' && (
            <div className="survey-step intro">
              <div className="survey-intro-copy">
                <p>
                  A través de los siguientes tests vas a poder conocer un poco más tu personalidad y
                  tus dones para acercarte de manera más efectiva a cumplir el propósito de Dios en
                  tu vida.
                </p>
                <p>
                  No hay respuestas correctas o incorrectas. Recuerda que no es quién quieres ser:
                  para obtener los mejores resultados responde con total sinceridad.
                </p>
              </div>
              <InitialSurveyForm
                set_student={set_student}
                student={student}
                surveys={allSurveys}
                campus={parsedQS?.campus}
                onLookupSuccess={handleLookupSuccess}
              />
            </div>
          )}

          {step === 'guestInfo' && (
            <div className="survey-step">
              <header className="survey-step__header">
                <h2>{isRetake ? 'Confirma tus datos' : 'Tus datos'}</h2>
                <p>
                  {isRetake
                    ? 'Revisa tu información y continúa para volver a responder la encuesta.'
                    : 'Por favor ingresa la siguiente información para continuar.'}
                </p>
              </header>
              <RegistrationForm>
                <Form
                  Attendant={student}
                  handleInputs={handleInputs}
                  errorInputs={errorInputs}
                  showGender
                  showBirthdate
                  showOcupation
                />
              </RegistrationForm>
              {Object.values(errorInputs).includes(true) && (
                <div className="survey-alert" role="alert">
                  <strong>Revisa los campos marcados</strong>
                  <p>Por favor verifica los campos que faltan por llenar.</p>
                </div>
              )}
              <div className="survey-habeas">{habeasData}</div>
              <div className="survey-actions">
                <Button
                  primary
                  onClick={() =>
                    verifyGuestForm() ? set_step('guestInfo') : set_step(allSurveys?.[0])
                  }
                >
                  Continuar
                </Button>
              </div>
            </div>
          )}

          {step === 'disc' &&
            renderSurveyStep({
              stepKey: 'disc',
              answers: DISCAnswers,
              setAnswers: set_DISCAnswers,
              errorKey: 'DISCAnswers',
              verify: verifyDISCForm,
            })}

          {step === 'spiritualgifts' &&
            renderSurveyStep({
              stepKey: 'spiritualgifts',
              answers: spiritualGiftAnswers,
              setAnswers: set_spiritualGiftAnswers,
              errorKey: 'spiritualGiftAnswers',
              verify: verifySpiritualGiftsForm,
            })}

          {step === 'experienceWithGod' &&
            renderSurveyStep({
              stepKey: 'experienceWithGod',
              answers: experienceWithGodAnswers,
              setAnswers: set_experienceWithGodAnswers,
              errorKey: 'experienceWithGodAnswers',
              verify: verifyExperienceWithGodForm,
            })}

          {step === 'additionalInformation' && (
            <div className="survey-step disc">
              <div className="additional-questions-form">
                <header className="survey-step__header">
                  <h2>{SURVEY_META.additionalInformation.title}</h2>
                  <p>{SURVEY_META.additionalInformation.intro}</p>
                </header>
                {errorInputs['additionalInformation'] && questionsLeftBanner()}
                {renderQuestions({
                  questions: renderAdditionalQuestions(),
                  errorInputs,
                  inputs: additionalInformation,
                  handleInputs: (e) => {
                    if (e.target.name && e.target.value) {
                      set_additionalInformation({
                        ...additionalInformation,
                        [e.target.name]: e.target.value,
                      });
                    }
                  },
                })}
                <div className="survey-actions">
                  <Button primary onClick={() => goNextSurvey('additionalInformation')}>
                    Continuar
                  </Button>
                </div>
              </div>
            </div>
          )}

          {step === 'sending' && (
            <div className="quoteSending">
              <div className="sending-spinner" aria-hidden="true" />
              <h2>
                “¡Gracias por hacerme tan maravillosamente complejo! Tu fino trabajo es maravilloso,
                lo sé muy bien.”
              </h2>
              <p className="sending-verse">Salmo 139:14 (NTV)</p>
              <p className="sending-status">Enviando tus respuestas…</p>
            </div>
          )}

          {step === 'results' && (
            <div className="survey-step survey-results">
              <header className="survey-step__header">
                <h2>
                  {viewingPrevious
                    ? 'Tus resultados de la última vez que respondiste'
                    : 'Tus resultados'}
                </h2>
                <p>
                  {viewingPrevious
                    ? 'Encontramos tu encuesta. Puedes guardar el PDF o si deseas, volver a responderla.'
                    : 'Guarda o descarga un PDF con el resumen de tus encuestas.'}
                </p>
              </header>
              {viewingPrevious && completedSurveys.length > 0 && (
                <div className="previous-survey-badge">
                  Completaste:{' '}
                  {completedSurveys
                    .map((key) => FLOW_LABELS[key] || SURVEY_META[key]?.short || key)
                    .join(', ')}
                </div>
              )}
              <div className="survey-actions survey-actions--top">
                <Button primary onClick={() => toPDF()}>
                  Guardar resultados
                </Button>
              </div>
              <InPersonStudentResults
                DISCAnswers={DISCAnswers}
                experienceWithGodAnswers={experienceWithGodAnswers}
                spiritualGiftAnswers={spiritualGiftAnswers}
                parsedQS={parsedQS}
                student={student}
                surveyDate={surveyDate}
              />
              {viewingPrevious && (
                <div className="survey-retake">
                  <h3>¿Quieres volver a realizar la encuesta?</h3>
                  <p>
                    Si respondes de nuevo, actualizaremos tus resultados con las nuevas respuestas.
                  </p>
                  <Button primary onClick={startRetake}>
                    Volver a responder
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Clase3Component>
  );
};

export default Survey;
